/**
 * F-31 × F-27: the link card's own entry in the selection toolbar (docs/08 §2
 * — an element type's folder owns its renderer AND its toolbar entry, so the
 * toolbar never special-cases a type).
 *
 * Selecting a card puts an "Open in browser" button in the pill. That is the
 * SINGLE-click path to the URL now that a plain click on the card only selects
 * it (a double-click on the card itself is the other path — ElementsLayer),
 * and it is the whole path on touch, where a double-tap is an awkward thing to
 * ask of a finger.
 *
 * `visible`, not `enabled`: on any other element there is simply no link to
 * open, and a greyed button would only send people hunting for the setting
 * that turns it on — the registry's own rule for choosing between the two.
 */
import { registerToolbarAction, type ToolbarActionContext } from "../../toolbar-actions/index.js";
import { linkCardUrl, openLinkInBrowser } from "./open.js";

/**
 * The URL the current selection would open, or null. Re-read from the doc on
 * every render AND at click time rather than captured once: the pill sits open
 * across remote edits, so a card that has since been deleted (C4-style id
 * re-check) must be judged as it is NOW.
 */
function selectedLinkUrl(ctx: ToolbarActionContext): string | null {
  const sel = ctx.selection;
  if (sel.kind !== "element") return null;
  const element = ctx.session.board.getElement(sel.elementId);
  return element === undefined ? null : linkCardUrl(element);
}

/** 16 px inline SVG, stroke = currentColor — the pill's icon convention. */
const iconOpenExternal = (
  <svg
    width={16}
    height={16}
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {/* Arrow leaving the box, then the box it leaves (open corner top-right). */}
    <path d="M9.5 2.5h4v4M13.5 2.5 7.75 8.25" />
    <path d="M11.5 9.5v3a1 1 0 0 1-1 1H3.5a1 1 0 0 1-1-1V5.5a1 1 0 0 1 1-1h3" />
  </svg>
);

registerToolbarAction({
  id: "link-card-open",
  appliesTo: "elements",
  tooltip: "Open in browser",
  // Ahead of bring-to-front / send-to-back (10/11): for a link card, opening
  // the link is THE action, so it takes the leftmost slot in the pill.
  order: 5,
  icon: iconOpenExternal,
  visible: (ctx) => selectedLinkUrl(ctx) !== null,
  run: (ctx) => {
    const url = selectedLinkUrl(ctx);
    if (url !== null) openLinkInBrowser(url);
  },
});
